import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ChevronLeft, ChevronRight, Search, Users,
  CheckCircle2, XCircle, Clock3, LogIn, LogOut, Sun,
} from "lucide-react";
import { toISODate, monthLabel, LEAVE_LABELS, LEAVE_SHORT, buildMonthGrid, isWeekend, WEEKDAY_HEADS } from "@/lib/attendance";
import { TableRowsSkeleton } from "@/components/LoadingSkeleton";

const LEAVE_COLORS = {
  CL: "bg-amber-100 text-amber-800 border-amber-200",
  ML: "bg-sky-100 text-sky-800 border-sky-200",
  half_day: "bg-violet-100 text-violet-800 border-violet-200",
};

function daysOfMonth(year, month0) {
  const days = [];
  const d = new Date(year, month0, 1);
  while (d.getMonth() === month0) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

function isWeekendDay(d) { return d.getDay() === 0 || d.getDay() === 6; }

// Iter 45.75 — role pill config + effective-role helper (multi-position
// aware). Leadership tiers (CM / TL / Admin / DA / SA) get a highlighted
// pill in the team-attendance grid; associates keep the plain text line.
const ATT_ROLE_RANK = { super_admin: 0, admin: 1, department_admin: 2, content_manager: 3, team_lead: 4, associate: 5 };
const LEADERSHIP_PILL = {
  super_admin:      { label: "Super Admin", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  admin:            { label: "Admin",       cls: "bg-purple-100 text-purple-700 border-purple-200" },
  department_admin: { label: "Dept Admin",  cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  content_manager:  { label: "CM",          cls: "bg-violet-100 text-violet-700 border-violet-200" },
  team_lead:        { label: "TL",          cls: "bg-sky-100 text-sky-700 border-sky-200" },
};
function effectiveAttRole(u) {
  const rs = [u?.role, ...((u?.positions || []).map((p) => p && p.role))].filter(Boolean);
  if (!rs.length) return "associate";
  return rs.reduce((best, r) => (ATT_ROLE_RANK[r] ?? 9) < (ATT_ROLE_RANK[best] ?? 9) ? r : best, rs[0]);
}

function StatusBadge({ status }) {
  if (status === "approved") return <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded"><CheckCircle2 className="h-2.5 w-2.5" /> Approved</span>;
  if (status === "rejected") return <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded"><XCircle className="h-2.5 w-2.5" /> Rejected</span>;
  if (status === "cancelled") return <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded"><XCircle className="h-2.5 w-2.5" /> Cancelled</span>;
  return <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded"><Clock3 className="h-2.5 w-2.5" /> Pending</span>;
}

function DayDot({ entry, leaves, weekend }) {
  if (weekend) return <span className="inline-block h-3 w-3 rounded-full bg-slate-100" title="Weekend" />;
  const active = (leaves || []).filter((l) => l.status !== "cancelled");
  const approvedLeave = active.find((l) => l.status === "approved");
  const pendingLeave = active.find((l) => l.status === "pending");
  const rejectedLeave = active.find((l) => l.status === "rejected");
  if (approvedLeave) {
    return (
      <span
        className={`inline-flex items-center justify-center h-4 w-4 rounded text-[8px] font-bold text-white ${approvedLeave.leave_type === "ML" ? "bg-sky-500" : approvedLeave.leave_type === "half_day" ? "bg-violet-500" : "bg-amber-500"}`}
        title={`${LEAVE_LABELS[approvedLeave.leave_type]} (approved)`}
      >
        {LEAVE_SHORT[approvedLeave.leave_type]}
      </span>
    );
  }
  if (entry?.holiday) return <span className="inline-flex items-center justify-center h-4 w-4 rounded bg-purple-500 text-white text-[8px] font-bold" title={entry.holiday_reason || "Holiday"}>H</span>;
  if (entry?.login_time || entry?.logout_time) return <span className="inline-block h-3 w-3 rounded-full bg-emerald-500" title={`${entry.login_time || "—"} → ${entry.logout_time || "—"}`} />;
  if (pendingLeave) return <span className="inline-block h-3 w-3 rounded-full bg-amber-300 border border-amber-500" title={`${LEAVE_LABELS[pendingLeave.leave_type]} (pending)`} />;
  if (rejectedLeave) return <span className="inline-block h-3 w-3 rounded-full bg-red-300 border border-red-500" title={`${LEAVE_LABELS[rejectedLeave.leave_type]} (rejected)`} />;
  return <span className="inline-block h-3 w-3 rounded-full bg-slate-100 border border-slate-200" />;
}

/* Iter 45.37 — Click-through: HR / CM / TL can click a teammate's name
   in the team-attendance grid to see their full monthly calendar with
   login / logout times, leaves (CL / ML / Half-Day with status), and
   holidays.  Read-only.  Data comes from the already-loaded
   /attendance/team payload — no extra API call. */
function UserAttendanceDialog({ user, cursor, entries, leaves, counts, onClose }) {
  if (!user) return null;
  const grid = buildMonthGrid(cursor.getFullYear(), cursor.getMonth());
  const c = counts[user.id] || { cl: 0, ml: 0, half_day: 0 };
  const entryByDate = {};
  for (const e of entries) entryByDate[e.date] = e;
  const leavesByDate = {};
  for (const lv of leaves) (leavesByDate[lv.date] = leavesByDate[lv.date] || []).push(lv);

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="user-attendance-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            {user.name}
            <span className="text-[11px] font-normal capitalize text-slate-500">· {(user.role || "").replace("_", " ")}</span>
          </DialogTitle>
          <DialogDescription className="flex items-center gap-3 text-xs">
            <span>{(user.departments || [user.department] || []).filter(Boolean).join(", ") || "—"}</span>
            <span className="text-slate-300">·</span>
            <span className="font-semibold">{monthLabel(cursor.getFullYear(), cursor.getMonth())}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Monthly leave totals */}
        <div className="flex flex-wrap gap-2 text-xs" data-testid="user-att-totals">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800" data-testid="user-att-cl">
            <span className="font-semibold">CL</span> {c.cl}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-sky-50 border border-sky-200 text-sky-800" data-testid="user-att-ml">
            <span className="font-semibold">ML</span> {c.ml}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-violet-50 border border-violet-200 text-violet-800" data-testid="user-att-half">
            <span className="font-semibold">Half</span> {c.half_day}
          </span>
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-7 gap-1 mt-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
          {WEEKDAY_HEADS.map((w) => <div key={w} className="px-1 py-0.5 text-center">{w}</div>)}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {grid.flat().map(({ date, outside }, idx) => {
            const iso = toISODate(date);
            const weekend = isWeekend(date);
            const entry = entryByDate[iso];
            const dayLeaves = (leavesByDate[iso] || []).filter((l) => l.status !== "cancelled");
            const approved = dayLeaves.find((l) => l.status === "approved");
            const pending  = dayLeaves.find((l) => l.status === "pending");
            const rejected = dayLeaves.find((l) => l.status === "rejected");

            let bg = "bg-white", border = "border-slate-200";
            if (weekend) { bg = "bg-slate-50"; border = "border-slate-100"; }
            if (outside) { bg = "bg-slate-50/50"; border = "border-slate-100"; }
            if (approved) { bg = "bg-emerald-50/70"; border = "border-emerald-300"; }
            else if (pending)  { bg = "bg-amber-50/70";   border = "border-amber-300"; }
            else if (rejected) { bg = "bg-red-50/70";     border = "border-red-300"; }
            else if (entry?.holiday) { bg = "bg-purple-50/70"; border = "border-purple-300"; }

            return (
              <div
                key={idx}
                data-testid={`user-att-cell-${iso}`}
                className={`relative min-h-[80px] rounded border ${border} ${bg} p-1.5 ${outside ? "opacity-60" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-[11px] font-semibold ${outside ? "text-slate-300" : "text-slate-600"}`}>{date.getDate()}</span>
                  {weekend && !outside && <span className="text-[9px] uppercase tracking-wider text-slate-400">Off</span>}
                </div>
                <div className="mt-1 space-y-0.5">
                  {entry?.holiday && (
                    <div className="inline-flex items-center gap-1 text-[10px] px-1 rounded bg-purple-200 text-purple-900" title={entry.holiday_reason || "Holiday"}>
                      <Sun className="h-2.5 w-2.5" /> Holiday
                    </div>
                  )}
                  {entry?.login_time && (
                    <div className="flex items-center gap-1 text-[10px] text-emerald-700" data-testid={`user-att-login-${iso}`}>
                      <LogIn className="h-2.5 w-2.5" /> {entry.login_time}
                    </div>
                  )}
                  {entry?.logout_time && (
                    <div className="flex items-center gap-1 text-[10px] text-rose-700" data-testid={`user-att-logout-${iso}`}>
                      <LogOut className="h-2.5 w-2.5" /> {entry.logout_time}
                    </div>
                  )}
                  {dayLeaves.map((lv) => {
                    const label = LEAVE_SHORT[lv.leave_type] || lv.leave_type;
                    const color = lv.leave_type === "ML"
                      ? "bg-sky-100 text-sky-800"
                      : lv.leave_type === "half_day"
                        ? "bg-violet-100 text-violet-800"
                        : "bg-amber-100 text-amber-800";
                    return (
                      <div
                        key={lv.id}
                        className={`inline-flex items-center text-[10px] px-1 rounded ${color}`}
                        title={`${LEAVE_LABELS[lv.leave_type]} · ${lv.status}${lv.reason ? " — " + lv.reason : ""}`}
                      >
                        {label} · <span className="ml-0.5 capitalize">{lv.status}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function HRAttendance() {
  const { user } = useAuth();
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ users: [], entries: [], leaves: [] });
  const [q, setQ] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  // Iter 45.37 — Which teammate's calendar is open in the drill-in dialog.
  const [openUser, setOpenUser] = useState(null);

  const mk = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;

  const minMonthDate = useMemo(() => new Date(today.getFullYear(), today.getMonth() - 2, 1), []); // eslint-disable-line
  const maxMonthDate = useMemo(() => new Date(today.getFullYear(), today.getMonth(), 1), []); // eslint-disable-line
  const canGoPrev = cursor > minMonthDate;
  const canGoNext = cursor < maxMonthDate;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/attendance/team?month=${mk}`);
      setData(r.data || { users: [], entries: [], leaves: [] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [mk]); // eslint-disable-line

  const shift = (delta) => {
    const next = new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1);
    if (next < minMonthDate || next > maxMonthDate) return;
    setCursor(next);
  };

  const days = useMemo(() => daysOfMonth(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  // Index entries/leaves by user_id then date
  const entryIdx = useMemo(() => {
    const m = {};
    for (const e of data.entries) {
      (m[e.user_id] = m[e.user_id] || {})[e.date] = e;
    }
    return m;
  }, [data.entries]);

  const leaveIdx = useMemo(() => {
    const m = {};
    for (const lv of data.leaves) {
      const uid = lv.user_id;
      const day = lv.date;
      m[uid] = m[uid] || {};
      (m[uid][day] = m[uid][day] || []).push(lv);
    }
    return m;
  }, [data.leaves]);

  const filteredUsers = useMemo(() => {
    let list = data.users || [];
    // Exclude admin from grid? Keep admin visible to HR too.
    if (deptFilter !== "all") {
      list = list.filter((u) => (u.departments || [u.department]).includes(deptFilter));
    }
    const qs = q.trim().toLowerCase();
    if (qs) {
      list = list.filter((u) => (u.name || "").toLowerCase().includes(qs) || (u.email || "").toLowerCase().includes(qs));
    }
    // Sort by effective (highest) role — SA > Admin > DA > CM > TL > associates.
    list = [...list].sort((a, b) => (ATT_ROLE_RANK[effectiveAttRole(a)] ?? 9) - (ATT_ROLE_RANK[effectiveAttRole(b)] ?? 9) || (a.name || "").localeCompare(b.name || ""));
    return list;
  }, [data.users, q, deptFilter]);

  const decidedLeaves = useMemo(() => (data.leaves || []).filter((l) => l.status !== "pending"), [data.leaves]);
  const pendingLeaves = useMemo(() => (data.leaves || []).filter((l) => l.status === "pending"), [data.leaves]);

  return (
    <div className="max-w-[1500px] mx-auto p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Attendance — HR</h1>
          <p className="text-sm text-slate-500 mt-0.5">Team-wide attendance grid + leave decisions for the current month.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} disabled={!canGoPrev} className={`p-2 rounded-md border ${canGoPrev ? "border-slate-200 hover:bg-slate-100 text-slate-700" : "border-slate-100 text-slate-300 cursor-not-allowed"}`} data-testid="hr-att-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="text-sm font-semibold text-slate-800 min-w-[140px] text-center" data-testid="hr-att-month-label">{monthLabel(cursor.getFullYear(), cursor.getMonth())}</div>
          <button onClick={() => shift(1)} disabled={!canGoNext} className={`p-2 rounded-md border ${canGoNext ? "border-slate-200 hover:bg-slate-100 text-slate-700" : "border-slate-100 text-slate-300 cursor-not-allowed"}`} data-testid="hr-att-next-month">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search team member…" className="pl-9" data-testid="hr-att-search" />
        </div>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="h-9 w-44" data-testid="hr-att-dept-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            <SelectItem value="content">Content</SelectItem>
            <SelectItem value="seo">SEO</SelectItem>
            <SelectItem value="marketing">Marketing</SelectItem>
            <SelectItem value="hr">HR</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-3 text-[11px] text-slate-600">
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Present</span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-amber-500" /> CL</span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-sky-500" /> ML</span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-violet-500" /> Half</span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-purple-500" /> Holiday</span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-slate-100 border border-slate-300" /> Off</span>
        </div>
      </div>

      {/* Attendance grid */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="text-sm border-collapse">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 sticky left-0 bg-slate-50 z-20 min-w-[200px]">
                  <div className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Member</div>
                </th>
                {/* Iter 36.9.13 — Bug 20. Monthly leave totals per person. */}
                <th className="px-2 py-2 text-center text-[10px] font-semibold text-amber-700 min-w-[46px]" title="Approved Casual Leaves this month">CL</th>
                <th className="px-2 py-2 text-center text-[10px] font-semibold text-sky-700 min-w-[46px]" title="Approved Medical Leaves this month">ML</th>
                <th className="px-2 py-2 text-center text-[10px] font-semibold text-violet-700 min-w-[46px]" title="Approved Half-Days this month">HALF</th>
                {days.map((d) => (
                  <th key={toISODate(d)} className={`px-1 py-1 text-center text-[10px] font-semibold ${isWeekendDay(d) ? "text-slate-300" : "text-slate-500"} min-w-[26px]`} title={toISODate(d)}>
                    <div>{d.getDate()}</div>
                    <div className="text-[8px] uppercase tracking-wider">{d.toLocaleDateString("en-US", { weekday: "short" }).slice(0, 1)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <TableRowsSkeleton cols={days.length + 4} rows={6} firstAvatar />}
              {!loading && filteredUsers.length === 0 && (
                <tr><td colSpan={days.length + 4} className="text-center py-8 text-sm text-slate-400">No members match the filters.</td></tr>
              )}
              {!loading && filteredUsers.map((u) => {
                const c = data.counts?.[u.id] || { cl: 0, ml: 0, half_day: 0 };
                return (
                <tr key={u.id} className="border-t border-slate-100" data-testid={`hr-att-user-row-${u.id}`}>
                  <td className="px-3 py-2 sticky left-0 bg-white z-10 min-w-[200px]">
                    <button
                      type="button"
                      onClick={() => setOpenUser(u)}
                      className="text-left font-medium text-slate-800 truncate hover:text-purple-700 hover:underline transition"
                      data-testid={`hr-att-user-name-${u.id}`}
                    >
                      {u.name}
                    </button>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap" data-testid={`hr-att-role-line-${u.id}`}>
                      {LEADERSHIP_PILL[effectiveAttRole(u)] ? (
                        <span className={`inline-block text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border ${LEADERSHIP_PILL[effectiveAttRole(u)].cls}`} data-testid={`hr-att-role-pill-${u.id}`}>
                          {LEADERSHIP_PILL[effectiveAttRole(u)].label}
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-400 capitalize">{(u.role || "associate").replace("_", " ")}</span>
                      )}
                      <span className="text-[10px] text-slate-400 truncate capitalize">· {(u.departments || [u.department] || []).join(", ") || "—"}</span>
                    </div>
                  </td>
                  <td className="px-2 py-2 text-center text-xs font-semibold text-amber-700" data-testid={`hr-att-cl-${u.id}`}>{c.cl || 0}</td>
                  <td className="px-2 py-2 text-center text-xs font-semibold text-sky-700" data-testid={`hr-att-ml-${u.id}`}>{c.ml || 0}</td>
                  <td className="px-2 py-2 text-center text-xs font-semibold text-violet-700" data-testid={`hr-att-half-${u.id}`}>{c.half_day || 0}</td>
                  {days.map((d) => {
                    const iso = toISODate(d);
                    const e = entryIdx[u.id]?.[iso];
                    const lvs = leaveIdx[u.id]?.[iso] || [];
                    return (
                      <td key={iso} className={`px-0 py-1 text-center border-l border-slate-50 ${isWeekendDay(d) ? "bg-slate-50/60" : ""}`}>
                        <DayDot entry={e} leaves={lvs} weekend={isWeekendDay(d)} />
                      </td>
                    );
                  })}
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>

      {/* Leave decisions log */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="hr-att-pending">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3"><Clock3 className="h-4 w-4 text-amber-600" /> Pending leaves ({pendingLeaves.length})</h3>
          <div className="divide-y divide-slate-100 max-h-[300px] overflow-y-auto">
            {pendingLeaves.length === 0 && <div className="text-xs text-slate-400 py-4">No pending leaves this month.</div>}
            {pendingLeaves.map((lv) => (
              <div key={lv.id} className="py-2 flex items-center gap-2 text-sm">
                <span className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded border ${LEAVE_COLORS[lv.leave_type] || ""}`}>{LEAVE_LABELS[lv.leave_type]}</span>
                <span className="flex-1 truncate"><strong>{lv.user_name}</strong> · {lv.date}</span>
                <span className="text-[10px] text-slate-400">→ {lv.approver_name || "—"}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="hr-att-decisions">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Decisions this month ({decidedLeaves.length})</h3>
          <div className="divide-y divide-slate-100 max-h-[300px] overflow-y-auto">
            {decidedLeaves.length === 0 && <div className="text-xs text-slate-400 py-4">No decisions yet.</div>}
            {decidedLeaves.map((lv) => (
              <div key={lv.id} className="py-2 flex items-start gap-2 text-sm" data-testid={`hr-att-decision-${lv.id}`}>
                <span className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded border ${LEAVE_COLORS[lv.leave_type] || ""}`}>{LEAVE_LABELS[lv.leave_type]}</span>
                <div className="flex-1 min-w-0">
                  <div className="truncate"><strong>{lv.user_name}</strong> · {lv.date}</div>
                  {lv.decision_note && <div className="text-[11px] text-slate-500 italic truncate">“{lv.decision_note}”</div>}
                </div>
                <StatusBadge status={lv.status} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <UserAttendanceDialog
        user={openUser}
        cursor={cursor}
        entries={(data.entries || []).filter((e) => e.user_id === openUser?.id)}
        leaves={(data.leaves || []).filter((l) => l.user_id === openUser?.id)}
        counts={data.counts || {}}
        onClose={() => setOpenUser(null)}
      />
    </div>
  );
}
